import { useState } from "react";
import { useGetList, useRecordContext } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { Briefcase, DollarSign, TrendingUp, TrendingDown } from "lucide-react";
import { JobStatusBadge } from "./JobStatusBadge";
import { JobDetail } from "./JobDetail";
import type { BldrOsContact } from "../types";

interface Job {
  id: string;
  job_number: string;
  status: string;
  title: string;
  estimated_total: number;
  actual_total: number;
  margin: number;
  margin_pct: number;
  created_at: string;
}

function fmt(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(n);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

interface ContactJobListProps {
  contactId?: string | number;
}

/**
 * Job list for a specific contact.
 * Renders inside the ContactShowTabs "Jobs" tab.
 */
export function ContactJobList({ contactId }: ContactJobListProps) {
  const record = useRecordContext<BldrOsContact>();
  const resolvedId = contactId ?? record?.id;

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isPending, total } = useGetList<Job>("jobs", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "created_at", order: "DESC" },
    filter: resolvedId ? { contact_id: resolvedId } : {},
  });

  if (!resolvedId) {
    return (
      <div className="text-sm text-muted-foreground py-4">
        No contact selected.
      </div>
    );
  }

  if (selectedId) {
    return (
      <JobDetail
        jobId={selectedId}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold flex items-center gap-1.5">
          <Briefcase className="w-4 h-4" />
          Jobs
          {total !== undefined && total > 0 && (
            <span className="text-xs text-muted-foreground font-normal">
              ({total})
            </span>
          )}
        </h4>
      </div>

      {isPending && (
        <div className="text-sm text-muted-foreground py-4">
          Loading jobs…
        </div>
      )}

      {!isPending && (!data || data.length === 0) && (
        <Card>
          <CardContent className="p-6 text-center">
            <Briefcase className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-40" />
            <p className="text-sm text-muted-foreground">
              No jobs yet. Jobs are created automatically when an estimate is
              accepted.
            </p>
          </CardContent>
        </Card>
      )}

      {data && data.length > 0 && (
        <div className="space-y-2">
          {data.map((job) => {
            const isPositive = Number(job.margin) >= 0;
            const TrendIcon = isPositive ? TrendingUp : TrendingDown;
            return (
              <Card
                key={job.id}
                className="cursor-pointer hover:border-blue-200 transition-colors"
                onClick={() => setSelectedId(job.id)}
              >
                <CardContent className="p-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-mono text-muted-foreground">
                          {job.job_number}
                        </span>
                        <JobStatusBadge status={job.status} />
                      </div>
                      <p className="text-sm font-medium truncate">
                        {job.title || "Untitled"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(job.created_at)}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold tabular-nums flex items-center gap-1">
                        <DollarSign className="w-3.5 h-3.5 text-muted-foreground" />
                        {fmt(Number(job.estimated_total))}
                      </p>
                      <p
                        className="text-xs font-medium tabular-nums flex items-center gap-0.5 justify-end"
                        style={{
                          color: isPositive ? "#16a34a" : "#dc2626",
                        }}
                      >
                        <TrendIcon className="w-3 h-3" />
                        {Number(job.margin_pct).toFixed(0)}% margin
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
